/**
 * GlobalSearchDialog — Recherche globale du site.
 *
 * Ouvre un dialog plein écran contenant un input de recherche instantané
 * qui filtre articles magazine, enquêtes, livrets, tutos et pages clés.
 *
 * • Trigger : bouton loupe (composant `GlobalSearchTrigger`)
 * • Raccourci : Ctrl+K / Cmd+K (⌘K) ouvre le dialog
 * • Résultats groupés par catégorie, cliquables (navigate + fermeture auto)
 * • État vide : suggestions de dossiers populaires
 *
 * Utilise React Router `useNavigate` pour naviguer vers l'URL cible.
 * Full-screen sur mobile pour maximiser la surface tactile.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FileText,
  BookOpen,
  Newspaper,
  Route,
  Video,
  Library,
  Command,
  X,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useGlobalSearchIndex, filterSearchEntries } from '../hooks/useGlobalSearchIndex';

const KIND_ICON = {
  magazine: Newspaper,
  enquete: FileText,
  ressource: Library,
  page: Route,
};

const KIND_COLOR = {
  magazine: '#cf2031',
  enquete: '#f59e0b',
  ressource: '#16a34a',
  page: '#6366f1',
};

/** Suggestions par défaut quand la recherche est vide. */
const SUGGESTIONS_DEFAUT = [
  { label: 'Casque', q: 'casque' },
  { label: 'Livret plateau', q: 'plateau' },
  { label: 'Distances de sécurité', q: 'distances' },
  { label: 'Assurance moto', q: 'assurance' },
  { label: 'Baromètre ONISR', q: 'baromètre' },
  { label: 'EPI', q: 'epi' },
  { label: 'Permis A2', q: 'A2' },
  { label: 'Freinage', q: 'freinage' },
];

export function GlobalSearchDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const entries = useGlobalSearchIndex();
  const results = query.trim().length >= 2 ? filterSearchEntries(entries, query) : [];
  const hasQuery = query.trim().length >= 2;

  // Auto-focus input à l'ouverture
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setSelectedIndex(0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Reset sélection à chaque changement de query
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = useCallback(
    (entry) => {
      onOpenChange(false);
      setTimeout(() => navigate(entry.url), 150);
    },
    [navigate, onOpenChange]
  );

  // Navigation clavier ↑ ↓ Enter
  const handleKeyDown = useCallback(
    (e) => {
      if (!hasQuery || results.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const target = results[selectedIndex];
        if (target) handleSelect(target);
      }
    },
    [hasQuery, results, selectedIndex, handleSelect]
  );

  // Grouper par catégorie
  const grouped = results.reduce((acc, r) => {
    (acc[r.category] = acc[r.category] || []).push(r);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-2xl w-[95vw] sm:w-full max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="global-search-dialog"
      >
        {/* Accessibilité : titre visible uniquement pour les lecteurs d'écran */}
        <DialogTitle className="sr-only">Recherche sur le site</DialogTitle>

        {/* Input de recherche */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
          <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Rechercher un dossier, une enquête, une question..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground"
            data-testid="global-search-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 h-6 px-1.5 rounded border border-border text-[10px] font-bold text-muted-foreground bg-muted">
            ESC
          </kbd>
        </div>

        {/* Résultats OU suggestions */}
        <div
          ref={listRef}
          className="overflow-y-auto flex-1 max-h-[70vh]"
          data-testid="global-search-results"
        >
          {/* Cas 1 : Requête vide → Suggestions */}
          {!hasQuery && (
            <div className="p-5">
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                Suggestions rapides
              </div>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS_DEFAUT.map((s) => (
                  <button
                    key={s.q}
                    onClick={() => setQuery(s.q)}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border-2 border-border hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 hover:text-[hsl(var(--primary))] text-sm font-semibold transition-all"
                    data-testid={`search-suggestion-${s.q}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-border text-xs text-muted-foreground leading-relaxed space-y-1.5">
                <p>💡 Tape au moins 2 caractères pour lancer la recherche.</p>
                <p>
                  ↑↓ pour naviguer · <span className="font-bold">Entrée</span> pour ouvrir ·{' '}
                  <span className="font-bold">Échap</span> pour fermer
                </p>
              </div>
            </div>
          )}

          {/* Cas 2 : Aucun résultat */}
          {hasQuery && results.length === 0 && (
            <div className="p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                <Search className="w-7 h-7 text-muted-foreground" />
              </div>
              <h3 className="text-base font-black mb-1">Aucun résultat</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
                Aucun contenu ne correspond à «&nbsp;<strong>{query}</strong>&nbsp;». Essaie un autre mot-clé
                ou consulte notre <button className="underline font-bold text-[hsl(var(--primary))]" onClick={() => { onOpenChange(false); setTimeout(() => navigate('/magazine'), 100); }}>hub magazine complet</button>.
              </p>
            </div>
          )}

          {/* Cas 3 : Résultats groupés */}
          {hasQuery && results.length > 0 && (
            <div className="py-2">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <div className="px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground bg-muted/40">
                    {category} · {items.length}
                  </div>
                  {items.map((item) => {
                    const Icon = KIND_ICON[item.kind] || FileText;
                    const color = KIND_COLOR[item.kind] || '#64748b';
                    const globalIdx = results.findIndex((r) => r.id === item.id);
                    const isSelected = globalIdx === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(globalIdx)}
                        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                          isSelected
                            ? 'bg-[hsl(var(--primary))]/8'
                            : 'hover:bg-muted/50'
                        }`}
                        data-testid={`search-result-${item.id}`}
                      >
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: `${color}15`,
                            color,
                          }}
                        >
                          <Icon className="w-4.5 h-4.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-sm font-black leading-snug text-foreground line-clamp-1">
                              {item.title}
                            </h4>
                            {item.badge && (
                              <span
                                className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0"
                                style={{
                                  backgroundColor: `${color}15`,
                                  color,
                                }}
                              >
                                {item.badge}
                              </span>
                            )}
                          </div>
                          {item.subtitle && (
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-snug">
                              {item.subtitle}
                            </p>
                          )}
                        </div>
                        <ArrowRight className={`w-4 h-4 flex-shrink-0 self-center transition-all ${isSelected ? 'text-[hsl(var(--primary))] translate-x-1' : 'text-muted-foreground/40'}`} />
                      </button>
                    );
                  })}
                </div>
              ))}
              <div className="px-4 py-2 text-[10px] text-muted-foreground border-t border-border bg-muted/20 flex items-center justify-between">
                <span>{results.length} résultat{results.length > 1 ? 's' : ''}</span>
                <span className="hidden sm:inline">
                  ↑↓ naviguer · Entrée pour ouvrir
                </span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * GlobalSearchTrigger — Bouton loupe qui ouvre le dialog.
 * Le raccourci ⌘K / Ctrl+K est aussi enregistré globalement.
 * Variants: 'icon' (compact mobile), 'full' (barre de recherche desktop).
 */
export function GlobalSearchTrigger({ variant = 'icon', className = '' }) {
  const [open, setOpen] = useState(false);

  // Raccourci clavier global ⌘K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (variant === 'full') {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-border bg-card hover:bg-muted/50 hover:border-[hsl(var(--primary))]/40 transition-all text-sm text-muted-foreground min-w-[240px] ${className}`}
          data-testid="global-search-trigger-full"
          aria-label="Ouvrir la recherche globale"
        >
          <Search className="w-4 h-4" />
          <span className="flex-1 text-left">Rechercher un dossier…</span>
          <kbd className="hidden md:inline-flex items-center gap-0.5 h-5 px-1.5 rounded border border-border text-[10px] font-bold bg-muted">
            <Command className="w-3 h-3" /> K
          </kbd>
        </button>
        <GlobalSearchDialog open={open} onOpenChange={setOpen} />
      </>
    );
  }

  // variant === 'icon'
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-[hsl(var(--primary))]/10 hover:text-[hsl(var(--primary))] text-foreground/80 transition-all ${className}`}
        data-testid="global-search-trigger-icon"
        aria-label="Ouvrir la recherche globale (Ctrl+K)"
        title="Rechercher (Ctrl+K)"
      >
        <Search className="w-5 h-5" />
      </button>
      <GlobalSearchDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

export default GlobalSearchTrigger;
